import { useNavigate } from "react-router";
import { ArrowLeftIcon } from "vega-icons";

import { Tooltip } from "@vega-core/components/Tooltip/Tooltip";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { ThemedButton } from "../../Buttons/ThemedButton";
import { MobileDrawer } from "../../MobileDrawer/MobileDrawer";

export type NavigationItem = {
  href: string;
  name: string;
  isActive?: boolean;
  disabled?: boolean;
  tooltip?: string;
};

export type MobileBreadcrumbsDrawerProps = {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  onNavigateBack?: () => void;
  backButtonText?: string;
  navigationItems: NavigationItem[];
  description?: string;
};

export const MobileBreadcrumbsDrawer = ({
  isOpen,
  onOpenChange,
  className,
  onNavigateBack,
  backButtonText,
  navigationItems,
  description,
}: MobileBreadcrumbsDrawerProps) => {
  const navigate = useNavigate();

  const handleBackButtonClick = () => {
    if (onNavigateBack) {
      onNavigateBack();
    } else {
      navigate(-1);
    }
  };

  const handleNavigationClick = (href: string) => {
    navigate(href);
    onOpenChange?.(false);
  };

  return (
    <MobileDrawer
      className={className}
      description={description}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      {onNavigateBack && backButtonText ? (
        <ThemedButton
          className="text-left underline"
          dataTestId="mobile-drawer-back-button"
          icon={<ArrowLeftIcon className="h-5 w-5" />}
          iconPosition="left"
          onClick={handleBackButtonClick}
          variant="tertiary"
        >
          {backButtonText}
        </ThemedButton>
      ) : null}
      {navigationItems.map((item, index) => (
        <div
          className={cnTwMerge("w-full rounded-default  cursor-pointer", {
            "hover:bg-background/secondary": !item.disabled,
          })}
          key={item.href}
          onClick={() => handleNavigationClick(item.href)}
        >
          <ThemedButton
            dataTestId={`mobile-drawer-item-${index}`}
            disabled={item.disabled}
            variant="tertiary"
          >
            {item.tooltip ? (
              <Tooltip
                content={item.tooltip}
                contentProps={{ side: "right" }}
                key={item.name}
                providerProps={{ delayDuration: 0 }}
              >
                <span className="cursor-not-allowed">{item.name}</span>
              </Tooltip>
            ) : (
              item.name
            )}
          </ThemedButton>
        </div>
      ))}
    </MobileDrawer>
  );
};
